import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8 || !/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) {
      setError("Password must be at least 8 characters and include letters and numbers.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — you can sign in now.");
      setTimeout(() => navigate("/diagnostic/login", { replace: true }), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset your password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9fafb] px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#e5e7eb] p-8 sm:p-10">
        <div className="flex flex-col items-center text-center gap-3">
          <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" />
          <h1 className="text-xl font-bold text-[#111827]">Choose a new password</h1>
        </div>

        {!token ? (
          <div className="mt-6 rounded-lg border border-[#fde68a] bg-[#fffbeb] p-4 text-[13px] text-[#92400e] flex gap-2" data-testid="reset-password-no-token">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>This reset link is missing its token. Please use the link from your email, or request a new one from the sign-in page.</span>
          </div>
        ) : done ? (
          <div className="mt-6 rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] p-4 text-[13px] text-[#065f46] flex gap-2" data-testid="reset-password-success">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Your password has been updated. Redirecting you to sign in…</span>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">New password</label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password"
                className="h-11 bg-white border-[#e5e7eb] text-[#111827]" data-testid="reset-password-new" autoFocus />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">Confirm password</label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password"
                className="h-11 bg-white border-[#e5e7eb] text-[#111827]" data-testid="reset-password-confirm" />
            </div>
            <p className="text-[12px] text-[#6b7280]">At least 8 characters, with letters and numbers.</p>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-[12px] text-red-700" data-testid="reset-password-error">{error}</div>
            )}
            <Button type="submit" disabled={busy}
              className="w-full bg-[#007a9e] hover:bg-[#00607d] text-white h-11 mt-1" data-testid="reset-password-submit">
              {busy ? "Updating…" : "Update password"}
            </Button>
            <button type="button" onClick={() => navigate("/diagnostic/login")}
              className="w-full text-[12px] text-[#6b7280] hover:text-[#111827] mt-1" data-testid="reset-password-back">
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
